/**
 * SA Tools - Proposal Team Section
 * Manage team members for IPA report
 */
import React, { useState } from 'react';
import { Paper, Title, Text, TextInput, Stack, Button, Group, Table, ActionIcon, Modal } from '@mantine/core';
import { IconPlus, IconTrash, IconEdit } from '@tabler/icons-react';
import { defaultTeamMember } from '../../utils/constants';

// ============================================================================
// Uncomment imports below when Microsoft Graph API permissions are granted
// ============================================================================
// import { Loader, Alert } from '@mantine/core';
// import { IconSearch, IconUserPlus } from '@tabler/icons-react';
// import { notifications } from '@mantine/notifications';
// import { useAuth } from '../../../../shared/contexts/AuthContext';
// import { getUserByEmail, getCurrentUser } from '../../../../shared/services/graphService';

function ProposalTeamSection({ context, setContext }) {
  const [editingMember, setEditingMember] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);

  // ============================================================================
  // Uncomment state below when Microsoft Graph API permissions are granted
  // ============================================================================
  // const [lookupEmail, setLookupEmail] = useState('');
  // const [isLookingUp, setIsLookingUp] = useState(false);
  // const { getAccessToken } = useAuth();

  const teamMembers = context.proposal_team || [];

  const handleAddMember = () => {
    setEditingMember({ ...defaultTeamMember });
    setEditingIndex(null);
    setModalOpened(true);
  };

  const handleEditMember = (index) => {
    setEditingMember({ ...teamMembers[index] });
    setEditingIndex(index);
    setModalOpened(true);
  };

  const handleRemoveMember = (index) => {
    const newTeamMembers = teamMembers.filter((_, i) => i !== index);
    setContext({ ...context, proposal_team: newTeamMembers });
  };

  const handleSaveMember = () => {
    let newTeamMembers;
    if (editingIndex !== null) {
      // Update existing member
      newTeamMembers = [...teamMembers];
      newTeamMembers[editingIndex] = editingMember;
    } else {
      // Add new member
      newTeamMembers = [...teamMembers, editingMember];
    }
    setContext({ ...context, proposal_team: newTeamMembers });
    setModalOpened(false);
    setEditingMember(null);
    setEditingIndex(null);
  };

  const handleFieldChange = (field, value) => {
    setEditingMember({ ...editingMember, [field]: value });
  };

  // ============================================================================
  // Uncomment functions below when Microsoft Graph API permissions are granted
  // ============================================================================
  // const handleLookupByEmail = async () => {
  //   if (!lookupEmail || !lookupEmail.trim()) {
  //     notifications.show({
  //       title: 'Email Required',
  //       message: 'Please enter an email address to lookup',
  //       color: 'yellow',
  //     });
  //     return;
  //   }
  //
  //   setIsLookingUp(true);
  //   try {
  //     const userData = await getUserByEmail(lookupEmail.trim(), getAccessToken);
  //
  //     if (userData) {
  //       setEditingMember({
  //         ...editingMember,
  //         ...userData,
  //       });
  //       setLookupEmail('');
  //       notifications.show({
  //         title: 'User Found',
  //         message: `Successfully loaded information for ${userData.name}`,
  //         color: 'green',
  //       });
  //     } else {
  //       notifications.show({
  //         title: 'User Not Found',
  //         message: 'No user found with that email address. You can still enter information manually.',
  //         color: 'yellow',
  //       });
  //     }
  //   } catch (error) {
  //     console.error('Error looking up user:', error);
  //     notifications.show({
  //       title: 'Lookup Failed',
  //       message: 'Failed to lookup user information. Please enter manually.',
  //       color: 'red',
  //     });
  //   } finally {
  //     setIsLookingUp(false);
  //   }
  // };
  //
  // const handleAddCurrentUser = async () => {
  //   setIsLookingUp(true);
  //   try {
  //     const userData = await getCurrentUser(getAccessToken);
  //     setEditingMember({
  //       ...defaultTeamMember,
  //       ...userData,
  //     });
  //     setEditingIndex(null);
  //     setModalOpened(true);
  //     notifications.show({
  //       title: 'Profile Loaded',
  //       message: 'Your profile information has been loaded',
  //       color: 'green',
  //     });
  //   } catch (error) {
  //     console.error('Error loading current user:', error);
  //     notifications.show({
  //       title: 'Failed to Load Profile',
  //       message: 'Could not load your profile. Please enter manually.',
  //       color: 'red',
  //     });
  //     setEditingMember({ ...defaultTeamMember });
  //     setEditingIndex(null);
  //     setModalOpened(true);
  //   } finally {
  //     setIsLookingUp(false);
  //   }
  // };

  return (
    <Paper shadow="sm" p="xl" radius="md">
      <Stack gap="lg">
        <div>
          <Title order={2} c="purple">Proposal Team</Title>
          <Text c="dimmed" size="sm" mt="xs">
            Add team members who participated in the IPA workshop and will be listed in the report.
          </Text>
        </div>

        <Group>
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            color="purple"
            onClick={handleAddMember}
          >
            Add Team Member
          </Button>
          {/* Uncomment when Microsoft Graph API permissions are granted
          <Button
            leftSection={<IconUserPlus size={16} />}
            variant="outline"
            color="purple"
            onClick={handleAddCurrentUser}
            loading={isLookingUp}
          >
            Add Myself
          </Button>
          */}
        </Group>

        {teamMembers.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No team members added yet. Click "Add Team Member" to get started.
          </Text>
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Company</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Phone</Table.Th>
                <Table.Th style={{ width: '100px' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {teamMembers.map((member, index) => (
                <Table.Tr key={index}>
                  <Table.Td>{member.name || '-'}</Table.Td>
                  <Table.Td>{member.title || '-'}</Table.Td>
                  <Table.Td>{member.company || '-'}</Table.Td>
                  <Table.Td>{member.email || '-'}</Table.Td>
                  <Table.Td>{member.phone || '-'}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="light"
                        color="purple"
                        onClick={() => handleEditMember(index)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => handleRemoveMember(index)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        {!context.customer && (
          <Text size="sm" c="dimmed">
            Save customer context first before adding team members
          </Text>
        )}
      </Stack>

      {/* Edit/Add Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={editingIndex !== null ? 'Edit Team Member' : 'Add Team Member'}
        size="lg"
      >
        <Stack gap="md">
          {/* ============================================================================
          Email Lookup Section - Uncomment when Microsoft Graph API permissions are granted
          ============================================================================
          {editingIndex === null && (
            <Alert color="blue" title="Quick Lookup" icon={<IconSearch size={16} />}>
              <Stack gap="sm">
                <Text size="sm">
                  Enter an email address to automatically populate team member information from your organization's directory.
                </Text>
                <Group>
                  <TextInput
                    placeholder="email@example.com"
                    value={lookupEmail}
                    onChange={(e) => setLookupEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleLookupByEmail();
                      }
                    }}
                    style={{ flex: 1 }}
                    disabled={isLookingUp}
                  />
                  <Button
                    leftSection={isLookingUp ? <Loader size="xs" /> : <IconSearch size={16} />}
                    onClick={handleLookupByEmail}
                    loading={isLookingUp}
                    color="blue"
                  >
                    Lookup
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}
          */}

          <TextInput
            label="Name"
            placeholder="Enter name"
            value={editingMember?.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            required
          />
          <TextInput
            label="Title"
            placeholder="Enter title/role"
            value={editingMember?.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
          />
          <TextInput
            label="Company"
            placeholder="Enter company name"
            value={editingMember?.company || ''}
            onChange={(e) => handleFieldChange('company', e.target.value)}
          />
          <TextInput
            label="Email"
            placeholder="Enter email address"
            type="email"
            value={editingMember?.email || ''}
            onChange={(e) => handleFieldChange('email', e.target.value)}
          />
          <TextInput
            label="Phone"
            placeholder="Enter phone number"
            type="tel"
            value={editingMember?.phone || ''}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setModalOpened(false)}>
              Cancel
            </Button>
            <Button color="purple" onClick={handleSaveMember}>
              {editingIndex !== null ? 'Save Changes' : 'Add Member'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}

export default ProposalTeamSection;
